import { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { CaretDown, MagnifyingGlass, UserCircle, Check } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { useT } from '@/i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Searchable employee picker (plain absolute menu, no portal). */
export default function BorrowerPicker({ value, onSelect }) {
  const { t } = useT();
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    axios.get(`${API}/employees`).then((r) => setEmployees(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return employees.slice(0, 50);
    return employees.filter((e) =>
      (e.name || '').toLowerCase().includes(s)
      || String(e.code || '').includes(s)
      || String(e.id_card || '').includes(s)
      || (e.department_name || '').toLowerCase().includes(s)).slice(0, 50);
  }, [q, employees]);

  return (
    <div className="relative" ref={ref} data-testid="scanner-borrower-picker">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="scanner-borrower-btn"
        className="w-full flex items-center gap-2 h-11 px-3 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors text-left">
        <UserCircle size={18} weight="duotone" className="text-violet-500 shrink-0" />
        <span className={`flex-1 text-sm truncate ${value ? 'text-zinc-800 font-medium' : 'text-zinc-400'}`}>
          {value ? value.name : t('scanner.borrower.select')}
        </span>
        <CaretDown size={14} className={`text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-1.5 rounded-xl border border-zinc-200 bg-white shadow-lg z-50 overflow-hidden" data-testid="scanner-borrower-menu">
          <div className="p-2 border-b border-zinc-200 flex items-center gap-2">
            <MagnifyingGlass size={15} className="text-zinc-400 shrink-0" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('scanner.borrower.search')}
              className="h-8 border-0 shadow-none focus-visible:ring-0 px-0 text-sm" data-testid="scanner-borrower-search" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && <p className="px-3 py-4 text-sm text-zinc-400 text-center">—</p>}
            {filtered.map((e) => (
              <button key={e.id} type="button" data-testid={`scanner-borrower-opt-${e.id}`}
                onClick={() => { onSelect(e); setOpen(false); setQ(''); }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-zinc-800 truncate">{e.name}</span>
                  <span className="block text-[11px] text-zinc-400 truncate">{[e.code, e.department_name].filter(Boolean).join(' · ')}</span>
                </span>
                {value?.id === e.id && <Check size={15} weight="bold" className="text-violet-500 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
